import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Gestión de usuarios del panel (HU-003 Fase 4) — CRUD + rol + import Excel,
 * todo contra endpoints de `usuario/views.py` que ya existían (el único
 * nuevo es `cambiarRol`, agregado en la Fase 2). Siempre manda el JWT del
 * personal logueado vía `tokenManager` — a diferencia de los hooks del
 * ciudadano (`useIncidencias`/`useArchivos`), acá si no hay token real la
 * API ya responde 401/403 (HU-003 Fase 1), así que no hace falta cubrir el
 * caso "sin token" con un fallback silencioso.
 */
const useUsuarios = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    ...extra,
  });

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      // DRF devuelve errores de validación como { campo: ["mensaje", ...] }
      // (sin `error`/`detail`) — se arma un texto legible a partir de eso en
      // vez de caer siempre en el mensaje genérico.
      const erroresDeCampo = Object.entries(data)
        .filter(([campo]) => campo !== 'error' && campo !== 'detail')
        .map(([campo, mensajes]) => `${campo}: ${Array.isArray(mensajes) ? mensajes.join(', ') : mensajes}`)
        .join(' — ');
      throw new Error(data.error || data.detail || erroresDeCampo || 'Error inesperado del servidor');
    }
    return data;
  };

  const listarUsuarios = async ({ rol = '', activo = '' } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (rol) params.set('rol', rol);
      if (activo !== '') params.set('activo', activo);
      const url = params.toString() ? `${API_CIFRA.usuario.listar}?${params}` : API_CIFRA.usuario.listar;

      const response = await fetch(url, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, usuarios: data.usuarios, total: data.total };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const crearUsuario = async (datos) => {
    setLoading(true);
    setError(null);
    try {
      // `registro_usuario` es público y siempre crea con rol CIUDADANO (HU-003
      // Fase 2 — `usu_chRol` es de solo lectura en el serializer). Si el
      // administrador eligió otro rol en el formulario, se aplica aparte con
      // `cambiarRol` justo después de crear.
      const response = await fetch(API_CIFRA.usuario.registro, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(datos),
      });
      const usuarioCreado = await manejarRespuesta(response);

      if (datos.usu_chRol && datos.usu_chRol !== 'CIUDADANO') {
        await cambiarRol(usuarioCreado.usu_inId, datos.usu_chRol);
        usuarioCreado.usu_chRol = datos.usu_chRol;
      }

      return { success: true, usuario: usuarioCreado };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const actualizarUsuario = async (id, datos) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.usuario.detalle(id), {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datos),
      });
      const data = await manejarRespuesta(response);
      return { success: true, usuario: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const cambiarRol = async (id, nuevoRol) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.usuario.cambiarRol(id), {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ usu_chRol: nuevoRol }),
      });
      const data = await manejarRespuesta(response);
      return { success: true, usuario: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const eliminarUsuario = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.usuario.detalle(id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok && response.status !== 204) {
        await manejarRespuesta(response);
      }
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const importarExcel = async (archivo) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const response = await fetch(API_CIFRA.usuario.importarExcel, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const data = await manejarRespuesta(response);
      return { success: true, resultado: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const descargarTemplate = async () => {
    try {
      const response = await fetch(API_CIFRA.usuario.descargarTemplate, { headers: authHeaders() });
      if (!response.ok) throw new Error('No se pudo descargar la plantilla');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_usuarios.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  return {
    loading,
    error,
    listarUsuarios,
    crearUsuario,
    actualizarUsuario,
    cambiarRol,
    eliminarUsuario,
    importarExcel,
    descargarTemplate,
  };
};

export default useUsuarios;
